/**
 * Ambient store for per-prescription field edits made on the summary card.
 *
 * Mirrors `prescriptionFlagsStore`: the editable summary fields live deep in the
 * component tree, but the edited values need to reach the send flow. The card
 * writes edits here; the send path reads them at send time to build an
 * update-then-send instruction (see PrescriptionSummary Send handler).
 *
 * Keyed by patientId so switching patients (without a full reload) never leaks
 * one patient's edits into another's send. Matching to medication rows is done by
 * normalized medication name.
 *
 * Cleared on logout via a full page reload (module state is recreated), same as
 * the flags / conversation / session stores.
 */

export interface PrescriptionEdits {
  drugName: string;
  dosage: string;
  route: string;
  form: string;
  sig: string;
  quantity: string;
  refills: string;
  substitutions: string;
  pharmacy: string;
}

// Human phrasing for each editable field, used to compose the update instruction the
// orchestration agent applies before sending/saving (so card edits reach the backend).
export const FIELD_PHRASE: Record<keyof PrescriptionEdits, string> = {
  drugName: "name",
  dosage: "strength",
  route: "route",
  form: "form",
  sig: "SIG",
  quantity: "quantity",
  refills: "refills",
  substitutions: "substitutions",
  pharmacy: "pharmacy",
};

/** Minimal shape of a prescription/draft as surfaced on the summary card. */
export interface EditableFieldsSource {
  medication_name?: string;
  drugName?: string;
  dosage?: string;
  route_of_administration?: string;
  dosage_form?: string;
  qualifier_types?: string;
  sig?: string;
  quantity?: string | number;
  refills?: string | number;
  substitutions?: string;
  pharmacy?: string;
}

/** The server-baseline edit values for a prescription (i.e. as if never touched). */
export const defaultEditsFromPrescription = (
  p: EditableFieldsSource,
  preferredPharmacy = ""
): PrescriptionEdits => ({
  drugName: (p.medication_name || p.drugName || "").toString(),
  dosage: (p.dosage || "").toString(),
  route: (p.route_of_administration || "").toString(),
  form: (p.dosage_form ?? p.qualifier_types ?? "").toString(),
  sig: (p.sig || "").toString(),
  quantity: (p.quantity ?? "").toString(),
  refills: (p.refills ?? 0).toString(),
  substitutions: (p.substitutions || "").toString(),
  pharmacy: ((p.pharmacy || "").trim() || preferredPharmacy || "").toString(),
});

const fieldsEqual = (a: string, b: string): boolean =>
  (a ?? "").toString().trim() === (b ?? "").toString().trim();

/**
 * Field values the server explicitly REJECTED for a medication, keyed by editable card
 * field: e.g. `{ form: "Capsule" }` after the catalog turned out to stock that drug only
 * as a tablet. Only the exact rejected value is retired -- a later, different edit to the
 * same field is a fresh request and must still reach the server.
 */
export type RejectedFieldValues = Partial<Record<keyof PrescriptionEdits, string>>;

/**
 * Normalize a dosage-form string for rejection comparison.
 *
 * MCP's invalidation stores the extractor's token (e.g. ``Suspension``) while the
 * clinician's card edit / chat text is often the full phrase (``oral suspension``).
 * Exact string equality then fails to retire the dead edit, which is what left the
 * summary card showing "oral suspension" after the server had already refused it.
 * Stripping a leading "oral " is the only synonym we need: route lives in its own
 * field, so "Oral Tablet" and "Tablet" are the same form for this purpose.
 */
const normalizeFormForRejection = (value: string): string =>
  (value ?? "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^oral\s+/, "");

const valueWasRejected = (
  rejected: RejectedFieldValues | undefined,
  field: keyof PrescriptionEdits,
  value: string
): boolean => {
  const rejectedValue = rejected?.[field];
  if (rejectedValue === undefined) return false;
  if (field === "form") {
    const a = normalizeFormForRejection(rejectedValue);
    const b = normalizeFormForRejection(value);
    return Boolean(a) && Boolean(b) && a === b;
  }
  return (
    (rejectedValue ?? "").toString().trim().toLowerCase() ===
    (value ?? "").toString().trim().toLowerCase()
  );
};

/**
 * Merge a stored edit onto the current server baseline, field by field.
 *
 * A field is kept from `stored` only if it was actually touched (differs from
 * `storedBaseline`, the server values in effect when the edit was captured) AND that
 * field's OWN baseline value hasn't changed since (`storedBaseline[field] === freshBaseline
 * [field]`). Every other field — untouched, or touched but whose baseline moved on — takes
 * the fresh value.
 *
 * Per-field, not whole-object: comparing the whole snapshot for equality would discard a
 * still-valid SIG edit just because an unrelated field (e.g. pharmacy, changed by a separate
 * "change pharmacy to X" turn) moved on in the same baseline. Only the specific field whose
 * own identity changed server-side (e.g. dosage, after the draft's catalog match was
 * re-resolved to a different strength/drugId) should lose its edit — see the wrong-strength
 * card-display bug this guards against.
 *
 * `rejected` closes the one hole in that baseline-movement rule: when the server REJECTS an
 * edit (the requested value doesn't exist in the catalog) it returns the field exactly as it
 * was, so `storedBaseline[field] === freshBaseline[field]` still holds and the dead edit would
 * be kept forever — indistinguishable from "the server hasn't caught up yet". A rejection is
 * reported structurally (`field_corrections` on the result payload) precisely because the
 * values alone cannot express it.
 */
export const mergeFreshBaselineWithStaleEdits = (
  stored: PrescriptionEdits,
  storedBaseline: PrescriptionEdits,
  freshBaseline: PrescriptionEdits,
  rejected?: RejectedFieldValues
): PrescriptionEdits => {
  const merged: PrescriptionEdits = { ...freshBaseline };
  (Object.keys(stored) as Array<keyof PrescriptionEdits>).forEach((field) => {
    const wasTouched = !fieldsEqual(stored[field], storedBaseline[field]);
    if (!wasTouched) return;
    const fieldBaselineUnchanged = fieldsEqual(
      storedBaseline[field],
      freshBaseline[field]
    );
    if (!fieldBaselineUnchanged) return;
    if (valueWasRejected(rejected, field, stored[field])) return;
    merged[field] = stored[field];
  });
  return merged;
};

/**
 * Same rejection filter as above, for the defensive path where no captured baseline exists
 * (so there is nothing to diff "touched" against) and the raw edits would be shown as-is.
 */
export const dropRejectedEdits = (
  edits: PrescriptionEdits,
  freshBaseline: PrescriptionEdits,
  rejected?: RejectedFieldValues
): PrescriptionEdits => {
  if (!rejected) return edits;
  const out: PrescriptionEdits = { ...edits };
  (Object.keys(edits) as Array<keyof PrescriptionEdits>).forEach((field) => {
    if (valueWasRejected(rejected, field, edits[field])) {
      out[field] = freshBaseline[field];
    }
  });
  return out;
};

interface StoredEdit {
  edits: PrescriptionEdits;
  // The server-baseline values in effect at the moment `edits` was captured. Edits are
  // keyed only by drug name (see module docstring), so without this a stale edit made
  // against one resolved strength/drugId would silently keep overriding every later
  // card for that same drug name even after the draft's own catalog match moved on
  // (e.g. a strength edit typed once, then the draft got re-resolved to a different
  // drugId/dosage in a later turn) -- regardless of what the server now says.
  baseline: PrescriptionEdits;
}

type ByName = Record<string, StoredEdit>;

let store: Record<string, ByName> = {};

const normName = (name: string): string =>
  (name || "").trim().replace(/\s+/g, " ").toLowerCase();

/** Record the current edited field values for one medication (by name) under a patient. */
export const setPrescriptionEdits = (
  patientId: string,
  medicationName: string,
  edits: PrescriptionEdits,
  baseline: PrescriptionEdits
): void => {
  const p = patientId || "";
  const key = normName(medicationName);
  if (!key) return;
  store[p] = store[p] || {};
  store[p][key] = { edits: { ...edits }, baseline: { ...baseline } };
};

/**
 * Return the edits for one medication (by name) under a patient, or undefined.
 *
 * When `currentBaseline` is supplied, each stored field is kept only if it was actually
 * touched and its own baseline hasn't moved since the edit was captured (see
 * mergeFreshBaselineWithStaleEdits) -- e.g. a SIG edit survives a later pharmacy change,
 * but a dosage edit is dropped once the draft's catalog match re-resolves to a different
 * strength. Without `currentBaseline`, the raw stored edits are returned unmerged.
 */
export const getPrescriptionEdits = (
  patientId: string,
  medicationName: string,
  currentBaseline?: PrescriptionEdits
): PrescriptionEdits | undefined => {
  const p = patientId || "";
  const byName = store[p];
  if (!byName) return undefined;
  const key = normName(medicationName);
  const stored = byName[key];
  if (!stored) return undefined;
  if (!currentBaseline) return stored.edits;
  return mergeFreshBaselineWithStaleEdits(
    stored.edits,
    stored.baseline,
    currentBaseline,
    getRejectedFields(patientId, medicationName)
  );
};

/** Drop all stored edits (e.g. on logout / session reset). */
export const clearPrescriptionEdits = (): void => {
  store = {};
  consumedClauses = {};
  rejectedFields = {};
};

// Module-level for the same reason `store` and `consumedClauses` are: a rejection has to
// outlive PrescriptionSummary remounts, and — more importantly — it must NOT be something
// the card's own mirroring effect can overwrite. That effect keeps re-writing the still-set
// local `editState` back into `store` on every unrelated prop update, so deleting the dead
// edit from `store` would just get undone on the next render. Recording the rejection
// separately and filtering on READ makes it un-resurrectable: every reader
// (getPrescriptionEdits, the card's display merge, buildUpdateClauseForPrescription) goes
// through the same filter regardless of what wrote the edit back.
let rejectedFields: Record<string, Record<string, RejectedFieldValues>> = {};

/** Server field names (result payload / medication rows) -> editable card field names. */
const SERVER_FIELD_TO_EDIT_FIELD: Record<string, keyof PrescriptionEdits> = {
  medication_name: "drugName",
  dosage: "dosage",
  route_of_administration: "route",
  dosage_form: "form",
  qualifier_types: "form",
  SIG_value: "sig",
  medication_quantity: "quantity",
  refills: "refills",
};

/** Record that the server rejected these exact values for one medication. */
export const markFieldsRejected = (
  patientId: string,
  medicationName: string,
  rejected: RejectedFieldValues
): void => {
  const p = patientId || "";
  const key = normName(medicationName);
  if (!key || !rejected || Object.keys(rejected).length === 0) return;
  rejectedFields[p] = rejectedFields[p] || {};
  rejectedFields[p][key] = { ...(rejectedFields[p][key] || {}), ...rejected };
};

/** The values the server has rejected for one medication, if any. */
export const getRejectedFields = (
  patientId: string,
  medicationName: string
): RejectedFieldValues | undefined => {
  const byName = rejectedFields[patientId || ""];
  if (!byName) return undefined;
  return byName[normName(medicationName)];
};

const mapServerRejectedValues = (
  values: unknown
): RejectedFieldValues => {
  const mapped: RejectedFieldValues = {};
  if (!values || typeof values !== "object") return mapped;
  Object.entries(values as Record<string, unknown>).forEach(
    ([serverField, value]) => {
      const editField = SERVER_FIELD_TO_EDIT_FIELD[serverField];
      const text = String(value ?? "").trim();
      if (editField && text) mapped[editField] = text;
    }
  );
  return mapped;
};

/**
 * Record a result payload's ``field_corrections`` (services/ui_blocks.py's
 * backfill_field_corrections): one entry per medication whose invalid update the server
 * auto-resolved to the only valid catalog candidate. Tolerates a missing/!array value so
 * every result message can call it unconditionally.
 *
 * Also accepts ``medication_data`` so a turn that left ``invalid_field_values`` on the
 * row (picker path — auto-resolve did not fire) still retires the clinician's dead
 * inline edit. Both signals describe the same structural fact; reading only
 * ``field_corrections`` left the card showing the refused form whenever the catalog
 * returned multiple candidates.
 */
export const recordServerFieldCorrections = (
  patientId: string,
  corrections: unknown,
  medicationData?: unknown
): void => {
  if (Array.isArray(corrections)) {
    corrections.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      const row = entry as {
        medication_name?: unknown;
        rejected_field_values?: unknown;
      };
      const name = String(row.medication_name ?? "").trim();
      const mapped = mapServerRejectedValues(row.rejected_field_values);
      if (name && Object.keys(mapped).length > 0) {
        markFieldsRejected(patientId, name, mapped);
      }
    });
  }
  if (Array.isArray(medicationData)) {
    medicationData.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      const row = entry as {
        medication_name?: unknown;
        invalid_field_values?: unknown;
      };
      const name = String(row.medication_name ?? "").trim();
      const mapped = mapServerRejectedValues(row.invalid_field_values);
      if (name && Object.keys(mapped).length > 0) {
        markFieldsRejected(patientId, name, mapped);
      }
    });
  }
};

/**
 * Drop the stored edit for one medication once its update clause has actually been
 * included in an outgoing message (see buildUpdateClauseForPrescription's callers in
 * pages/AIPrescription/index.tsx).
 *
 * Without this, the only thing that ever "clears" a pending edit is
 * mergeFreshBaselineWithStaleEdits noticing the server baseline caught up with the
 * stored value on some LATER call — which depends on that same medication reappearing
 * with fresh data in a subsequent turn. A medication nobody mentions again for a while
 * keeps comparing the stored edit against a stale frozen snapshot, so the clause never
 * stops looking "pending" and gets silently re-prepended to every unrelated message the
 * user sends after it was already sent once. Clearing explicitly at the point of
 * consumption makes "sent once -> never resent" deterministic instead of dependent on
 * the conversation history happening to refresh that drug again.
 */
export const clearPrescriptionEditsForMedication = (
  patientId: string,
  medicationName: string
): void => {
  const p = patientId || "";
  const byName = store[p];
  if (!byName) return;
  const key = normName(medicationName);
  if (!key) return;
  delete byName[key];
};

// Module-level (NOT component state), for the same reason `store` above is: it must
// survive pages/AIPrescription/index.tsx unmounting/remounting on ordinary tab navigation
// (Medication History, Favourite Drugs, etc. -- see conversationUtils.ts's heldConversation
// docstring for why that remount is routine, not exceptional).
//
// This is the authoritative "already sent, don't resend" gate for collectPendingUpdateClauses
// in that page. It used to live in a component-local `useRef` there -- which gets wiped on
// every one of those remounts, while the two things that cause a consumed edit to LOOK
// pending again do NOT get wiped: this file's `store` (an edit clears here at consumption
// time, but PrescriptionSummary's card-mirroring effect keeps re-writing its own still-set
// local `editState` back into `store` on every unrelated `prescriptions` prop update -- see
// that component's docstring), and `store` itself. Net effect: switch away from the AI
// Prescription tab and back, then type anything unrelated, and an edit already sent minutes
// ago would get silently prepended and sent again. Keeping the "already consumed" memory in
// the same module-level lifetime as the state that resurrects it closes that gap.
let consumedClauses: Record<string, string> = {};

const consumedKey = (patientId: string, medicationName: string): string =>
  `${patientId || ""}::${normName(medicationName)}`;

/** True when this exact clause text was already sent for this medication and never superseded. */
export const wasEditClauseConsumed = (
  patientId: string,
  medicationName: string,
  clause: string
): boolean => consumedClauses[consumedKey(patientId, medicationName)] === clause;

/** Record that this clause text has now been sent for this medication. */
export const markEditClauseConsumed = (
  patientId: string,
  medicationName: string,
  clause: string
): void => {
  consumedClauses[consumedKey(patientId, medicationName)] = clause;
};

/**
 * Build an "update <drug>: field to value, ..." instruction clause for one prescription,
 * comparing its persisted edits (if any) against the prescription's own server-baseline
 * values. Returns "" when there are no persisted edits or none differ from the baseline —
 * callers should skip emitting an update clause in that case.
 *
 * Shared by the Send / Send-all buttons (PrescriptionSummary) and the Save-all trigger
 * (AIPrescription page auto-save-on-switch) so both flows read the same source of truth
 * instead of only the single-drug Send handler seeing edits.
 */
export const buildUpdateClauseForPrescription = (
  p: EditableFieldsSource,
  patientId: string,
  preferredPharmacy = ""
): string => {
  const originalName = (p.medication_name || p.drugName || "").trim();
  if (!originalName) return "";

  const base = defaultEditsFromPrescription(p, preferredPharmacy);
  const stored = getPrescriptionEdits(patientId, originalName, base);
  if (!stored) return "";

  const changed = (Object.keys(stored) as Array<keyof PrescriptionEdits>).filter(
    (k) => (stored[k] ?? "").toString().trim() !== (base[k] ?? "").toString().trim()
  );
  if (changed.length === 0) return "";

  const finalName = (stored.drugName || originalName).trim();
  // Pipe-delimited (not comma) so a legitimate comma inside a field value (e.g. a SIG
  // like "Take 1 tablet twice daily, with food") can't be mistaken for the field
  // separator by the backend's free-text parser. See PrescriptionSummary's
  // FIELD_JOIN_DELIMITER/escapeFieldValue for the matching single-drug Send path.
  const parts = changed.map(
    (k) =>
      `${FIELD_PHRASE[k]} to ${((stored[k] ?? "").toString().trim() || "(none)").replace(
        /\|/g,
        "\\|"
      )}`
  );
  return `update ${originalName || finalName}: ${parts.join(" | ")}`;
};
